import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { SortingState } from "@tanstack/react-table";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { listSalesInvoices } from "@/shared/api/sales";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";
import { DocListLegend } from "@/modules/core/components/DocListLegend";
import { ORDER_LIFECYCLE_LEGEND } from "@/modules/core/components/lifecycle-status";
import type { PartyFilter } from "@/modules/core/list-cell-actions";
import {
  AdminFilters,
  adminFilterControlClass,
} from "@/modules/shell/AdminFilters";
import { ActiveFilterChips } from "@/modules/shell/ActiveFilterChips";
import {
  paramOr,
  readPartyFilter,
  readQ,
  setOrDelete,
  writePartyFilter,
  LIST_SEARCH_DEBOUNCE_MS,
} from "@/modules/shell/list-url-filters";
import { useListUrlParams } from "@/modules/shell/use-list-url-params";
import { DataTable } from "@/modules/shell/data-table";
import {
  DataTableChrome,
  DeskListFilterReset,
} from "@/modules/shell/DataTableChrome";
import { useDeskListFilterReset } from "@/modules/shell/use-desk-list-filter-reset";
import {
  ListSearchStatus,
  listSearchOnlinePending,
} from "@/modules/shell/ListSearchStatus";
import { DeskListHeader } from "@/modules/shell/DeskListHeader";
import { DeskSubNav } from "@/modules/shell/DeskSubNav";
import { salesModuleTabs } from "@/modules/shell/desk-module-tabs";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { SavedListViewsControl } from "@/modules/shell/SavedListViewsControl";
import { takeListReturn } from "@/modules/shell/list-return-state";
import { useListRowFlash } from "@/modules/shell/use-list-row-flash";
import { salesInvoiceColumns } from "./sales-invoices-columns";

const PAGE_SIZE = 50;
const LIFECYCLES = ["all", "draft", "open", "void"] as const;

type SiLifecycleFilter = (typeof LIFECYCLES)[number];

export function SalesInvoicesPage() {
  const { companySlug } = useParams();
  const { searchParams, patchParams, replaceAllParams } = useListUrlParams();
  const [query, setQuery] = useState(() => readQ(searchParams));
  const [debouncedQ, setDebouncedQ] = useState(() => readQ(searchParams));
  const lifecycleFilter = paramOr(
    searchParams,
    "lifecycle",
    LIFECYCLES,
    "all",
  ) as SiLifecycleFilter;
  const setLifecycleFilter = (next: SiLifecycleFilter) =>
    patchParams((p) => setOrDelete(p, "lifecycle", next));
  const partyFilter = readPartyFilter(searchParams);
  const setPartyFilter = (next: PartyFilter | null) => {
    patchParams((p) => writePartyFilter(p, next));
  };
  const [sorting, setSorting] = useState<SortingState>([]);
  const sortDir = sorting[0]?.desc ? "desc" : "asc";
  const serverLifecycle =
    lifecycleFilter === "all" ? undefined : lifecycleFilter;
  const listPath = companySlug
    ? `/c/${companySlug}/sales/invoices`
    : "/sales/invoices";
  const listReturnHandled = useRef(false);
  const { flashId, flashRow } = useListRowFlash();

  useEffect(() => {
    const t = window.setTimeout(() => {
      const q = query.trim();
      setDebouncedQ(q);
      patchParams((p) => setOrDelete(p, "q", q));
    }, LIST_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query, patchParams]);

  const invoicesQuery = useInfiniteQuery({
    queryKey: queryKeys.salesInvoices.list(companySlug ?? "", {
      q: debouncedQ,
      lifecycle: serverLifecycle ?? "all",
      limit: PAGE_SIZE,
      sortDir,
    }),
    enabled: Boolean(companySlug),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      listSalesInvoices(companySlug!, {
        q: debouncedQ || undefined,
        lifecycle: serverLifecycle,
        limit: PAGE_SIZE,
        cursor: pageParam,
        sortDir,
        signal,
      }),
    getNextPageParam: (last) =>
      last.hasMore && last.nextCursor ? last.nextCursor : undefined,
  });
  const errorMsg = queryErrorMessage(
    invoicesQuery.error,
    invoicesQuery.isError,
  );
  useAuthRedirectOnQueryError(errorMsg);

  const columns = useMemo(
    () =>
      companySlug
        ? salesInvoiceColumns(companySlug, {
            listPath,
            partyFilter,
            setPartyFilter,
          })
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companySlug, listPath, partyFilter],
  );

  const invoices =
    invoicesQuery.data?.pages.flatMap((p) => p.invoices) ?? [];
  const visible = partyFilter
    ? invoices.filter((p) => p.customerId === partyFilter.id)
    : invoices;
  const total = invoicesQuery.data?.pages[0]?.total;
  const loading = invoicesQuery.isPending;
  const error = errorMsg && errorMsg !== "Not signed in" ? errorMsg : null;
  const filtersActive =
    Boolean(query.trim()) ||
    Boolean(partyFilter) ||
    lifecycleFilter !== "all";
  const filterReset = useDeskListFilterReset({
    patchParams,
    setQuery: setQuery,
    canResetToDefault: filtersActive,
    canClearAll: filtersActive,
  });
  const searchingOnline = listSearchOnlinePending({
    query,
    debouncedQ,
    isFetching: invoicesQuery.isFetching,
  });

  useEffect(() => {
    if (!companySlug || loading || listReturnHandled.current) return;
    listReturnHandled.current = true;
    const id = takeListReturn(listPath);
    if (id) flashRow(id);
  }, [companySlug, loading, listPath, flashRow]);

  if (!companySlug) return null;

  return (
    <PageFrame>
      <DataTableChrome>
        <DeskListFilterReset {...filterReset} />
        <DeskListHeader
          title="Sales invoices"
          description="Customer bills — create from an open sales order"
          tabs={
            <DeskSubNav
              placement="header"
              items={salesModuleTabs(companySlug)}
              aria-label="Sales views"
            />
          }
        />

        <AdminFilters>
          <input
            className={`${adminFilterControlClass} min-w-[12rem] flex-1`}
            placeholder="Search number or customer…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={loading && invoices.length === 0}
          />
          <select
            className={adminFilterControlClass}
            value={lifecycleFilter}
            onChange={(e) =>
              setLifecycleFilter(e.target.value as SiLifecycleFilter)
            }
          >
            <option value="all">All lifecycle</option>
            <option value="draft">Draft</option>
            <option value="open">Posted</option>
            <option value="void">Void</option>
          </select>
          <SavedListViewsControl
            listId="sales-invoices"
            companySlug={companySlug}
            currentQs={searchParams.toString()}
            onApplyQs={(qs) => {
              replaceAllParams(qs);
              const q = readQ(new URLSearchParams(qs));
              setQuery(q);
              setDebouncedQ(q);
            }}
          />
          <ActiveFilterChips
            chips={[
              ...(partyFilter
                ? [
                    {
                      key: "party",
                      label: `Customer: ${partyFilter.name}`,
                      onClear: () => setPartyFilter(null),
                    },
                  ]
                : []),
              ...(lifecycleFilter !== "all"
                ? [
                    {
                      key: "lifecycle",
                      label:
                        lifecycleFilter === "open"
                          ? "Posted"
                          : lifecycleFilter[0]!.toUpperCase() +
                            lifecycleFilter.slice(1),
                      onClear: () => setLifecycleFilter("all"),
                    },
                  ]
                : []),
            ]}
          />
        </AdminFilters>
        <ListSearchStatus online searching={searchingOnline} />
        {visible.length > 0 && total != null ? (
          <p className="mt-2 text-xs text-[var(--color-muted)]">
            Showing {visible.length}
            {total > invoices.length
              ? ` · loaded ${invoices.length} of ${total}`
              : ""}
          </p>
        ) : null}

        {error && (
          <Alert variant="destructive" className="mt-4">
            {error}
          </Alert>
        )}
        {loading && invoices.length === 0 && (
          <Alert variant="muted" className="mt-8">
            Loading invoices…
          </Alert>
        )}

        {!loading && visible.length === 0 && !error && (
          <div className="mt-8">
            <EmptyState
              title={
                filtersActive ? "No invoices match" : "No sales invoices yet"
              }
              description={
                filtersActive
                  ? "Try clearing search or lifecycle filter."
                  : "Invoice from an open sales order when you need a formal bill."
              }
              action={
                filtersActive ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => filterReset.onClearAll()}
                  >
                    Clear filters
                  </Button>
                ) : (
                  <Button asChild size="sm" variant="outline">
                    <Link
                      to={`/c/${companySlug}/sales/orders`}
                      className="no-underline"
                    >
                      Sales orders
                    </Link>
                  </Button>
                )
              }
            />
          </div>
        )}

        {visible.length > 0 ? (
          <>
            <DocListLegend kinds={ORDER_LIFECYCLE_LEGEND} />
            <DataTable
              onRefresh={() => void invoicesQuery.refetch()}
              listId="sales-invoices"
              companySlug={companySlug}
              columns={columns}
              data={visible}
              getRowId={(r) => r.id}
              sorting={sorting}
              onSortingChange={setSorting}
              highlightedRowId={flashId}
              defaultVisibility={{
                paymentDueOn: true,
                updatedAt: true,
              }}
            />
            {invoicesQuery.hasNextPage ? (
              <div className="mt-4 flex justify-center">
                <Button
                  type="button"
                  variant="outline"
                  disabled={invoicesQuery.isFetchingNextPage}
                  pending={invoicesQuery.isFetchingNextPage}
                  pendingLabel="Loading more…"
                  onClick={() => void invoicesQuery.fetchNextPage()}
                >
                  Load more
                </Button>
              </div>
            ) : null}
          </>
        ) : null}

        <p className="mt-8 text-sm">
          <Link to={`/c/${companySlug}/sales/orders`} className="no-underline">
            ← Sales orders
          </Link>
        </p>
      </DataTableChrome>
    </PageFrame>
  );
}
